<template>
  <div :id="id" class="modal fade" tabindex="-1" data-backdrop="static">
    <div class="modal-dialog modal-fluid">
      <div class="modal-content">
        <div class="modal-header">
          <modal-close-button @confirmed="schliessen" />
          <h4 class="modal-title">{{ titel }}</h4>
        </div>

        <div v-if="lauf" class="modal-body">
          <!-- Kopf: Objekt, Start, Dauer, Lauf-ID (9.5) -->
          <p class="kopf">
            <span v-if="lauf.objektArt">
              {{ objektArtName(lauf.objektArt) }} {{ lauf.objektId }} ·
            </span>
            <span v-if="lauf.ausloeser === 'zeitplan'">
              automatisch {{ startWort }} {{ lauf.gestartetAm || lauf.angefordertAm }} ·
            </span>
            <span v-else>
              {{ startWort }} {{ lauf.gestartetAm || lauf.angefordertAm }}
              <template v-if="lauf.gestartetVonName">von {{ lauf.gestartetVonName }}</template> ·
            </span>
            <span v-if="dauer">Dauer {{ dauer }} · </span>
            Lauf-ID <span class="mono">{{ lauf.id }}</span>
            <button type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(lauf.id)">
              Kopieren
            </button>
            <span v-if="lauf.test" class="status statusGrey ml-2 px-2">Testlauf</span>
          </p>

          <div v-if="laeuftBereits" class="panel mt-2">
            Dieser Lauf arbeitet schon, gestartet {{ lauf.gestartetAm || lauf.angefordertAm }}
            <template v-if="lauf.gestartetVonName">von {{ lauf.gestartetVonName }}</template>.
            Es wurde kein zweiter gestartet.
          </div>

          <!-- läuft und wartet -->
          <template v-if="zustand === 'laeuft' || zustand === 'wartet'">
            <div v-if="zustand === 'wartet' && stand.wartetAuf" class="panel mt-3">
              <strong>Wartet auf</strong>
              {{ wartetAufText }} · Lauf-ID <span class="mono">{{ stand.wartetAuf.laufId }}</span>
              <button type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(stand.wartetAuf.laufId)">
                Kopieren
              </button>
            </div>

            <div v-if="phasen.length" class="schritte mt-3">
              <div v-for="phase in phasen" :key="phase.kennung" class="schritt">
                <span class="punkt" :class="'punkt-' + phase.zustand">
                  <font-awesome-icon v-if="phase.zustand === 'fertig'" icon="fa-solid fa-check" />
                  <font-awesome-icon v-else-if="phase.zustand === 'laeuft'" icon="fa-solid fa-rotate" />
                </span>
                <span>{{ phase.bezeichnung }}</span>
                <strong v-if="phase.zustand === 'laeuft'">läuft</strong>
                <span v-else class="text-muted">{{ phase.zustand === "fertig" ? "fertig" : "wartet" }}</span>
              </div>
            </div>

            <div v-if="lauf.fortschrittGesamt" class="mt-3">
              <div class="font-weight-bold mb-1">{{ lauf.fortschritt || 0 }} von {{ lauf.fortschrittGesamt }}</div>
              <div class="progress">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  :style="{ width: prozent + '%' }"
                  :aria-valuenow="prozent"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {{ prozent }} %
                </div>
              </div>
            </div>

            <p v-if="zaehler.length" class="mt-3 mb-0">
              <strong>Bisher:</strong>
              <span v-for="(z, i) in zaehler" :key="z.key" :class="z.klasse">
                {{ z.anzahl }} {{ z.text }}<template v-if="i < zaehler.length - 1"> · </template>
              </span>
            </p>
            <p class="kopf mt-3">Du kannst dieses Fenster schliessen. Der Lauf arbeitet auf dem Server weiter.</p>
          </template>

          <!-- Fehler -->
          <template v-if="zustand === 'fehler'">
            <div class="alert alert-danger mt-3 mb-0">
              <strong>{{ lauf.meldung || "Der Lauf ist fehlgeschlagen." }}</strong>
            </div>

            <div v-if="phasen.length" class="mt-3">
              <span
                v-for="phase in phasen"
                :key="phase.kennung"
                class="status px-2 mr-1"
                :class="phase.kennung === lauf.phase ? 'statusDanger' : 'statusGrey'"
              >
                {{ phase.bezeichnung }}<template v-if="phase.kennung === lauf.phase">: fehlgeschlagen</template>
              </span>
            </div>

            <div class="fakten mt-3">
              <div v-if="lauf.code" class="fakt">
                <div class="fakt-titel">Fehlercode</div>
                <span class="mono">{{ lauf.code }}</span>
                <button type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(lauf.code)">Kopieren</button>
              </div>
              <div class="fakt">
                <div class="fakt-titel">Letzter Versuch</div>
                {{ lauf.beendetAm || "–" }}
              </div>
              <div class="fakt">
                <div class="fakt-titel">Gestartet</div>
                {{ gestartetText }}
              </div>
              <div class="fakt">
                <div class="fakt-titel">Lauf-ID</div>
                <span class="mono">{{ lauf.id }}</span>
                <button type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(lauf.id)">Kopieren</button>
              </div>
            </div>

            <p class="mt-3 mb-0">
              <template v-if="naechsterLauf">
                Der nächste automatische Lauf startet am {{ naechsterLauf.datum }} um {{ naechsterLauf.zeit }}.
              </template>
              <template v-else>Starte den Lauf erneut, wenn die Ursache behoben ist.</template>
            </p>
            <p class="kopf mt-1">Hält der Fehler an, gib dem Batix-Support die Lauf-ID an.</p>
          </template>

          <!-- Abbruch: von Hand oder durch einen Neustart (8.3) -->
          <div v-if="istEnde && lauf.status === 'ABGEBROCHEN'" class="panel mt-3">
            <template v-if="lauf.code === 'LAUF_NEUSTART'">Der Lauf wurde durch einen Neustart abgebrochen.</template>
            <template v-else>Der Lauf wurde von Hand abgebrochen.</template>
            Was bis dahin geschrieben war, bleibt.
          </div>

          <!-- Ergebnis: Fehler, nicht verarbeitet, geschrieben je Aktion, verworfen -->
          <template v-if="zustand === 'ergebnis'">
            <section v-if="ergebnis.fehler.gesamt" class="abschnitt mt-3">
              <div class="abschnitt-kopf">
                <span class="symbol symbol-rot"><font-awesome-icon icon="fa-solid fa-xmark" /></span>
                <div class="abschnitt-titel">{{ ergebnis.fehler.gesamt }} {{ ergebnis.fehler.gesamt === 1 ? "Eintrag konnte" : "Einträge konnten" }} nicht geschrieben werden</div>
              </div>
              <div class="abschnitt-inhalt">
                <div v-for="e in ergebnis.fehler.eintraege" :key="e.id" class="zeile">
                  <div class="zeile-text">
                    <strong>{{ e.bezeichnung || e.objektId }}</strong>
                    <span class="text-muted ml-1">{{ objektArtName(e.objektArt) }}</span><br />
                    <span class="text-rot-normal">{{ e.meldung }}</span>
                    <span v-if="e.code" class="mono text-muted ml-1">{{ e.code }}</span>
                    <button v-if="e.code" type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(e.code)">Kopieren</button>
                  </div>
                  <button v-if="darfEintragErneut(e)" type="button" class="btn btn-default btn-sm" @click="eintragErneut(e)">
                    Erneut versuchen
                  </button>
                  <button v-if="linkZiel(e)" type="button" class="btn btn-default btn-sm" @click="objektOeffnen(e)">
                    {{ objektArtName(e.objektArt) }} öffnen
                  </button>
                </div>
                <button v-if="hatMehr(ergebnis.fehler)" type="button" class="btn btn-default btn-sm mt-2" @click="weitereLaden(ergebnis.fehler)">
                  Weitere laden
                </button>
              </div>
            </section>

            <section v-if="ergebnis.nichtVerarbeitet.gesamt" class="abschnitt mt-3">
              <div class="abschnitt-kopf">
                <span class="symbol symbol-bernstein"><font-awesome-icon icon="fa-solid fa-exclamation" /></span>
                <div class="abschnitt-titel">{{ ergebnis.nichtVerarbeitet.gesamt }} nicht verarbeitet</div>
              </div>
              <div class="abschnitt-inhalt">
                <div v-for="e in ergebnis.nichtVerarbeitet.eintraege" :key="e.id" class="zeile">
                  <div class="zeile-text">
                    <strong>{{ e.bezeichnung || e.objektId }}</strong>
                    <span class="text-muted ml-1">{{ objektArtName(e.objektArt) }}</span><br />
                    <span class="text-bernstein">{{ e.meldung }}</span>
                    <span v-if="e.code" class="mono text-muted ml-1">{{ e.code }}</span>
                    <button v-if="e.code" type="button" class="btn btn-default btn-xs ml-1" @click="kopieren(e.code)">Kopieren</button>
                  </div>
                  <button v-if="linkZiel(e)" type="button" class="btn btn-default btn-sm" @click="objektOeffnen(e)">
                    {{ objektArtName(e.objektArt) }} öffnen
                  </button>
                </div>
                <button v-if="hatMehr(ergebnis.nichtVerarbeitet)" type="button" class="btn btn-default btn-sm mt-2" @click="weitereLaden(ergebnis.nichtVerarbeitet)">
                  Weitere laden
                </button>
              </div>
            </section>

            <section v-for="gruppe in zugeklappteGruppen" :key="gruppe.key" class="abschnitt mt-3">
              <div class="abschnitt-kopf klickbar" @click="gruppeUmschalten(gruppe)">
                <span class="symbol symbol-grau">
                  <font-awesome-icon :icon="gruppe.reiter === 'VERWORFEN' ? 'fa-solid fa-minus' : 'fa-solid fa-check'" />
                </span>
                <div class="abschnitt-titel">{{ gruppe.anzahl }} {{ gruppe.text }}</div>
                <font-awesome-icon :icon="gruppe.offen ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="ml-auto text-muted" />
              </div>
              <div v-if="gruppe.offen" class="abschnitt-inhalt">
                <div v-for="e in gruppe.liste.eintraege" :key="e.id" class="zeile">
                  <div class="zeile-text">
                    <strong>{{ e.bezeichnung || e.objektId }}</strong>
                    <span class="text-muted ml-1">{{ objektArtName(e.objektArt) }}</span>
                    <span v-for="f in e.felder" :key="f.feld" class="badge badge-light ml-1">{{ feldName(f) }}</span>
                  </div>
                  <button v-if="linkZiel(e)" type="button" class="btn btn-default btn-sm" @click="objektOeffnen(e)">
                    {{ objektArtName(e.objektArt) }} öffnen
                  </button>
                </div>
                <button v-if="hatMehr(gruppe.liste)" type="button" class="btn btn-default btn-sm mt-2" @click="weitereLaden(gruppe.liste)">
                  Weitere laden
                </button>
              </div>
            </section>

            <p v-if="ergebnisGeladen && !hatErgebnisAbschnitte" class="kopf mt-3">Der Lauf hat keine Einträge.</p>
          </template>

          <!-- automatischer Lauf und Testlauf: Tabelle mit Reitern, Suche und Filtern, nur lesen -->
          <template v-if="zustand === 'automatisch' || zustand === 'testlauf'">
            <div v-if="zustand === 'testlauf'" class="alert alert-info mt-3 mb-0">
              <strong>Testlauf:</strong> Es wurde nichts geschrieben, gesendet oder gelöscht. Die Einträge zeigen, was ein
              echter Lauf täte.
            </div>

            <ul class="nav nav-tabs mt-3 reiter">
              <li v-for="r in reiterListe" :key="r.key" class="nav-item">
                <a class="nav-link" :class="{ active: tabelle.reiter === r.key }" href="#" @click.prevent="reiterWaehlen(r.key)">
                  {{ r.text }}<span class="reiter-zahl">{{ r.anzahl }}</span>
                </a>
              </li>
            </ul>
            <div class="block modal-liste br-t-l-0 p-2">
              <div class="row mb-2">
                <div class="col-xl-4">
                  <div class="form-group mb-0">
                    <input v-model="tabelle.suche" class="form-control" type="text" placeholder=" " />
                    <label>Suchen (Bezeichnung, Fremdnummer, Code)</label>
                  </div>
                </div>
                <div class="col-xl-3">
                  <div class="form-group mb-0">
                    <select v-model="tabelle.objektArt" class="form-control">
                      <option value="">Alle Objektarten</option>
                      <option v-for="(name, key) in objektArten" :key="key" :value="key">{{ name }}</option>
                    </select>
                    <label>Objektart</label>
                  </div>
                </div>
                <div class="col-xl-3">
                  <div class="form-group mb-0">
                    <select v-model="tabelle.aktion" class="form-control">
                      <option value="">Alle Aktionen</option>
                      <option v-for="a in aktionListe" :key="a.key" :value="a.key">{{ a.text }}</option>
                    </select>
                    <label>Aktion</label>
                  </div>
                </div>
              </div>

              <div class="tabelle-scroll">
                <b-table tbody-tr-class="item" :items="tabelle.eintraege" :fields="tabellenFelder" primary-key="id" show-empty empty-text="Keine Einträge">
                  <template #cell(objekt)="{ item }">
                    <a v-if="linkZiel(item)" href="#" @click.prevent="objektOeffnen(item)">{{ objektArtName(item.objektArt) }}</a>
                    <span v-else>{{ objektArtName(item.objektArt) }}</span>
                  </template>
                  <template #cell(bezeichnung)="{ item }">
                    <strong>{{ item.bezeichnung || item.objektId }}</strong>
                    <span v-if="item.fremdnummer" class="text-muted ml-1">{{ item.fremdnummer }}</span>
                  </template>
                  <template #cell(aktion)="{ item }">{{ item.aktionBezeichnung || item.aktion }}</template>
                  <template #cell(felder)="row">
                    <template v-if="row.item.felder.length">
                      <span v-for="f in row.item.felder" :key="f.feld" class="badge badge-light mr-1">{{ feldName(f) }}</span>
                      <button type="button" class="btn btn-default btn-xs" @click="row.toggleDetails">
                        {{ row.detailsShowing ? "zuklappen" : "aufklappen" }}
                      </button>
                    </template>
                  </template>
                  <template #cell(hinweis)="{ item }">
                    <span v-if="item.markierung" class="status statusWarn px-2 mr-1">{{ item.markierung }}</span>
                    <span v-if="item.status === 'FEHLER'" class="text-rot-normal">{{ item.meldung }}</span>
                    <span v-else-if="item.status === 'NICHT_VERARBEITET'" class="text-bernstein">{{ item.meldung }}</span>
                  </template>
                  <template #cell(status)="{ item }">
                    <span v-if="item.status === 'FEHLER'" class="status statusDanger px-2">{{ statusText(item.status) }}</span>
                    <span v-else-if="item.status === 'NICHT_VERARBEITET' || item.status === 'OFFEN'" class="status statusWarn px-2">{{ statusText(item.status) }}</span>
                    <span v-else>{{ statusText(item.status) }}</span>
                  </template>
                  <template #row-details="{ item }">
                    <table class="table table-sm mb-0 felder">
                      <thead>
                        <tr><th>Feld</th><th>bisher</th><th>neu</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="f in item.felder" :key="f.feld">
                          <td>{{ feldName(f) }}</td>
                          <td><span v-if="f.wertAlt" class="wert-alt">{{ f.wertAlt }}</span></td>
                          <td><span v-if="f.wertNeu" class="wert-neu">{{ f.wertNeu }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </b-table>
                <infinite-loading :identifier="tabelle.infiniteId" @infinite="tabelleNachladen">
                  <div slot="spinner">Lade weitere Einträge...</div>
                  <div slot="no-more"></div>
                  <div slot="no-results"></div>
                </infinite-loading>
              </div>
              <p class="kopf pt-2">Markierte stehen zuoberst.</p>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <!-- Links im Fuss, in jedem Zustand: der Lauf im Protokoll (G4, 9.5); auf dem Protokoll selbst nicht -->
          <button v-if="lauf && $route.name !== 'einstellungen-protokoll'" type="button" class="btn btn-default mr-auto" @click="imProtokollOeffnen">
            Im Protokoll öffnen
          </button>
          <template v-if="zustand === 'laeuft' || zustand === 'wartet'">
            <button v-if="stand.darfAbbrechen" type="button" class="btn btn-default" @click="abbrechen">
              Lauf abbrechen
            </button>
            <button type="button" class="btn btn-primary" @click="schliessen">Im Hintergrund weiterlaufen lassen</button>
          </template>
          <template v-else-if="zustand === 'fehler'">
            <button type="button" class="btn btn-default" @click="schliessen">Schliessen</button>
            <button v-if="stand.darfErneutAnfordern" type="button" class="btn btn-primary" @click="erneutVersuchen">
              <font-awesome-icon icon="fa-solid fa-rotate" class="mr-1" />
              Erneut versuchen
            </button>
          </template>
          <button v-else-if="zustand === 'ergebnis'" type="button" class="btn btn-success" @click="schliessen">OK</button>
          <button v-else type="button" class="btn btn-default" @click="schliessen">Schliessen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import ModalCloseButton from "@/components/global/ModalCloseButton.vue";
import { modal } from "@/mixins/Modal";
import InfiniteLoading from "vue-infinite-loading";
import { debounce } from "@/utils/Debouncer";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faRotate,
  faXmark,
  faExclamation,
  faMinus,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";

library.add(faCheck, faRotate, faXmark, faExclamation, faMinus, faChevronDown, faChevronRight);

// Stand alle zwei Sekunden abfragen, solange der Lauf arbeitet (6.4)
const ABFRAGE_MS = 2000;
const ARBEITET = ["WARTET", "LAEUFT", "WIRD_ANGEWENDET"];
// Fehler zeigt die Ansicht selbst mit apiErrorToAlert, ohne Umleitung auf die Fehlerseiten
const OHNE_UMLEITUNG = { redirectOnError: false };
// Seitengrösse von GET lauf/eintraege/ (9.5)
const SEITENGROESSE = 50;

// Allgemeine Status der Einträge (5.2) als Text in der Zeile und als Reiter
const STATUS_TEXT = {
  GESCHRIEBEN: "geschrieben",
  NICHT_VERARBEITET: "nicht verarbeitet",
  FEHLER: "Fehler",
  VERWORFEN: "verworfen",
  OFFEN: "offen",
  ZURUECKGESETZT: "zurückgesetzt",
  UEBERHOLT: "überholt",
};
const REITER = [
  { key: "alle", text: "Alle" },
  { key: "markiert", text: "Markiert" },
  { key: "GESCHRIEBEN", text: "Geschrieben" },
  { key: "NICHT_VERARBEITET", text: "Nicht verarbeitet" },
  { key: "FEHLER", text: "Fehler" },
  { key: "VERWORFEN", text: "Verworfen" },
  { key: "OFFEN", text: "Offen" },
  { key: "ZURUECKGESETZT", text: "Zurückgesetzt" },
  { key: "UEBERHOLT", text: "Überholt" },
];

function neueListe(reiter, aktion) {
  return { reiter, aktion: aktion || null, eintraege: [], seite: 0, gesamt: 0 };
}

function neuesErgebnis() {
  return { fehler: neueListe("FEHLER"), nichtVerarbeitet: neueListe("NICHT_VERARBEITET"), gruppen: [] };
}

function neueTabelle() {
  return {
    reiter: "alle",
    suche: "",
    objektArt: "",
    aktion: "",
    eintraege: [],
    seite: 0,
    infiniteId: +new Date(),
    anzahlJeReiter: {},
    anzahlJeAktion: {},
  };
}

// Deutsche Namen der Objektarten; unbekannte erscheinen wie geliefert
const OBJEKTARTEN = {
  auftrag: "Auftrag",
  person: "Person",
  firma: "Firma",
  mandant: "Mandant",
  bildungsverhaeltnis: "Bildungsverhältnis",
};

// Zähler im Stand, nur die über 0; Bernstein für „braucht Aufmerksamkeit", Rot für Fehler (9.1)
const ZAEHLER = [
  { key: "anzahlVerarbeitet", text: "verarbeitet" },
  { key: "anzahlGeschrieben", text: "geschrieben" },
  { key: "anzahlUnveraendert", text: "unverändert" },
  { key: "anzahlNichtVerarbeitet", text: "nicht verarbeitet", klasse: "text-bernstein" },
  { key: "anzahlFehler", text: "mit Fehler", klasse: "text-rot" },
  { key: "anzahlMarkiert", text: "markiert", klasse: "text-bernstein" },
  { key: "anzahlOffen", text: "offen", klasse: "text-bernstein" },
];

/**
 * Lauf-Ansicht für alle Abläufe (Konzept Steuerungen und Schnittstellen 9.5). Kennt keinen Ablauf:
 * Bezeichnungen, Phasen, Aktionen und erlaubte Knöpfe kommen vom Server. Geöffnet über den Event-Bus:
 * - lauf-ansicht-oeffnen { lauf } öffnet einen bestehenden Lauf,
 * - lauf-anfordern { ablauf, laufart, objektArt, objektId, test, parameter } fordert über POST lauf/ einen an.
 * Beim Schliessen meldet sie lauf-ansicht-geschlossen { lauf }, damit eine Seite Stand und Läufe neu lädt (G4).
 * Jede Seite bindet <lauf-ansicht /> höchstens einmal ein.
 */
export default {
  name: "LaufAnsicht",
  components: { ModalCloseButton, InfiniteLoading },
  mixins: [modal],
  data() {
    return {
      laufId: null,
      stand: null,
      laeuftBereits: false,
      abfrageTimer: null,
      ergebnis: neuesErgebnis(),
      ergebnisGeladen: false,
      tabelle: neueTabelle(),
      objektArten: OBJEKTARTEN,
      tabellenFelder: [
        { key: "objekt", label: "Objekt" },
        { key: "bezeichnung", label: "Bezeichnung" },
        { key: "aktion", label: "Aktion" },
        { key: "felder", label: "Felder" },
        { key: "hinweis", label: "Hinweis" },
        { key: "status", label: "Status" },
      ],
    };
  },
  computed: {
    // Beendet und kein Fehler: Ergebnis, automatischer Lauf oder Testlauf
    istEnde() {
      return !!this.lauf && !this.arbeitet && this.zustand !== "fehler";
    },
    zugeklappteGruppen() {
      return this.ergebnis.gruppen;
    },
    hatErgebnisAbschnitte() {
      return !!(this.ergebnis.fehler.gesamt || this.ergebnis.nichtVerarbeitet.gesamt || this.ergebnis.gruppen.length);
    },
    // Reiter mit Zahlen vom Server; leere entfallen, ausser Alle und dem gewählten
    reiterListe() {
      const anzahl = this.tabelle.anzahlJeReiter;
      return REITER.filter((r) => r.key === "alle" || r.key === this.tabelle.reiter || anzahl[r.key] > 0).map((r) => ({
        ...r,
        text: r.key === "GESCHRIEBEN" && this.zustand === "testlauf" ? "Würde geschrieben" : r.text,
        anzahl: anzahl[r.key] || 0,
      }));
    },
    aktionListe() {
      const aktionen = (this.stand && this.stand.aktionen) || {};
      const keys = new Set([...Object.keys(aktionen), ...Object.keys(this.tabelle.anzahlJeAktion)]);
      return [...keys].map((key) => ({ key, text: aktionen[key] || key }));
    },
    lauf() {
      return this.stand ? this.stand.lauf : null;
    },
    arbeitet() {
      return !!this.lauf && ARBEITET.includes(this.lauf.status);
    },
    // Welcher Zustand gilt (9.5); ZU_PRUEFEN und UEBERHOLT bis G6 wie Ergebnis
    zustand() {
      if (!this.lauf) return null;
      const status = this.lauf.status;
      if (status === "WARTET") return "wartet";
      if (status === "LAEUFT" || status === "WIRD_ANGEWENDET") return "laeuft";
      if (status === "FEHLGESCHLAGEN") return "fehler";
      if (this.lauf.test) return "testlauf";
      if (this.lauf.ausloeser === "zeitplan") return "automatisch";
      return "ergebnis";
    },
    titel() {
      if (!this.lauf) return "Lauf";
      return [this.lauf.ablaufBezeichnung || this.lauf.ablauf, this.lauf.laufartBezeichnung || this.lauf.laufart]
        .join(" · ");
    },
    dauer() {
      const s = this.lauf ? this.lauf.dauerSekunden : null;
      if (s === null || s === undefined) return null;
      const min = Math.floor(s / 60);
      return min > 0 ? `${min} Min. ${s % 60} s` : `${s} s`;
    },
    // Phasen als Schritte: vor der aktuellen fertig, die aktuelle läuft, danach wartet
    phasen() {
      if (!this.stand) return [];
      const aktuell = this.stand.phasen.findIndex((p) => p.kennung === this.lauf.phase);
      return this.stand.phasen.map((p, i) => ({
        ...p,
        zustand: this.zustand !== "laeuft" || aktuell < 0 || i > aktuell ? "wartet" : i < aktuell ? "fertig" : "laeuft",
      }));
    },
    prozent() {
      if (!this.lauf || !this.lauf.fortschrittGesamt) return 0;
      return Math.min(100, Math.round(((this.lauf.fortschritt || 0) * 100) / this.lauf.fortschrittGesamt));
    },
    zaehler() {
      if (!this.lauf) return [];
      return ZAEHLER.filter((z) => this.lauf[z.key] > 0).map((z) => ({ ...z, anzahl: this.lauf[z.key] }));
    },
    // Ein Lauf, der nur gewartet hat (auch abgebrochen, bevor er begann), hat kein gestartet_am
    startWort() {
      return this.lauf && this.lauf.gestartetAm ? "gestartet" : "angefordert";
    },
    gestartetText() {
      if (!this.lauf) return "";
      const zeit = this.lauf.gestartetAm || this.lauf.angefordertAm;
      if (this.lauf.ausloeser === "zeitplan") return `${zeit}, automatisch`;
      return this.lauf.gestartetVonName ? `${zeit}, ${this.lauf.gestartetVonName}` : zeit;
    },
    wartetAufText() {
      const w = this.stand ? this.stand.wartetAuf : null;
      if (!w) return "";
      let text = `${w.ablaufBezeichnung || w.ablauf} · ${w.laufartBezeichnung || w.laufart}`;
      if (w.gestartetVonName) text += `, gestartet von ${w.gestartetVonName}`;
      if (w.seit) text += `, seit ${w.seit}`;
      return text;
    },
    // naechsterLauf kommt als „dd.MM.yyyy HH:mm"
    naechsterLauf() {
      const wert = this.stand ? this.stand.naechsterLauf : null;
      if (!wert) return null;
      const [datum, zeit] = wert.split(" ");
      return { datum, zeit };
    },
  },
  watch: {
    // Endet der Lauf, während das Fenster offen ist, oder öffnet ein beendeter: Einträge laden
    zustand(neu) {
      if (neu === "ergebnis" && !this.ergebnisGeladen) this.ergebnisLaden();
    },
    "tabelle.reiter"() {
      this.tabelleNeuBeginnen();
    },
    "tabelle.objektArt"() {
      this.tabelleNeuBeginnen();
    },
    "tabelle.aktion"() {
      this.tabelleNeuBeginnen();
    },
    "tabelle.suche"() {
      this.sucheVerzoegert(this);
    },
  },
  mounted() {
    this.$bus.$on("lauf-ansicht-oeffnen", this.oeffnenHandler);
    this.$bus.$on("lauf-anfordern", this.anfordernHandler);
    // Schliessen mit Esc oder Hintergrund beendet die Abfrage ebenfalls
    $("#" + this.id).on("hidden.bs.modal", this.abfrageStoppen);
    $("#" + this.id).on("hidden.bs.modal", this.geschlossenMelden);
    // Die Tabelle lädt nur, wenn sie sichtbar ist: bei einem beendeten Lauf entsteht sie, bevor das Fenster eingeblendet ist
    $("#" + this.id).on("shown.bs.modal", this.tabelleSichtbar);
  },
  beforeDestroy() {
    this.$bus.$off("lauf-ansicht-oeffnen", this.oeffnenHandler);
    this.$bus.$off("lauf-anfordern", this.anfordernHandler);
    $("#" + this.id).off("hidden.bs.modal", this.abfrageStoppen);
    $("#" + this.id).off("hidden.bs.modal", this.geschlossenMelden);
    $("#" + this.id).off("shown.bs.modal", this.tabelleSichtbar);
    this.abfrageStoppen();
  },
  methods: {
    oeffnenHandler(payload) {
      this.laeuftBereits = false;
      this.oeffnen(payload && payload.lauf);
    },
    anfordernHandler(payload) {
      const anforderung = {
        ablauf: payload.ablauf,
        laufart: payload.laufart,
        objektArt: payload.objektArt || null,
        objektId: payload.objektId || null,
        test: !!payload.test,
        parameter: payload.parameter || {},
      };
      AppApi.post("lauf/", anforderung, OHNE_UMLEITUNG)
        .then((response) => {
          this.laeuftBereits = !!response.data.laeuftBereits;
          this.oeffnen(response.data.laufId);
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    oeffnen(laufId) {
      if (!laufId) return;
      this.abfrageStoppen();
      this.laufId = laufId;
      this.stand = null;
      this.ergebnis = neuesErgebnis();
      this.ergebnisGeladen = false;
      this.tabelle = neueTabelle();
      this.standLaden()
        .then(() => {
          if (!this.shown) this.openModal();
          this.abfragePlanen();
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    standLaden() {
      return AppApi.get("lauf/stand/", { ...OHNE_UMLEITUNG, params: { id: this.laufId } }).then((response) => {
        this.stand = response.data;
        this.abfragePlanen();
      });
    },
    abfragePlanen() {
      clearTimeout(this.abfrageTimer);
      this.abfrageTimer = null;
      // Eine Antwort, die nach dem Schliessen oder Abbauen kommt, plant keine weitere Abfrage
      if (!this.arbeitet || !this.shown || this._isDestroyed) return;
      this.abfrageTimer = setTimeout(() => {
        if (!this.shown) return;
        this.standLaden().catch((e) => this.$notify(apiErrorToAlert(e)));
      }, ABFRAGE_MS);
    },
    abfrageStoppen() {
      clearTimeout(this.abfrageTimer);
      this.abfrageTimer = null;
    },
    geschlossenMelden() {
      this.$bus.$emit("lauf-ansicht-geschlossen", { lauf: this.lauf ? this.lauf.id : null });
    },
    schliessen() {
      this.abfrageStoppen();
      this.closeModal();
    },
    /** Schliesst das Fenster und führt auf das Protokoll mit diesem Lauf (?lauf=) */
    imProtokollOeffnen() {
      this.schliessen();
      this.$router.push({
        name: "einstellungen-protokoll",
        query: { lauf: this.lauf.id },
      }).catch(() => {});
    },
    // Ein wartender endet sofort, ein laufender nach dem aktuellen Eintrag; die Abfrage zeigt das Ende
    abbrechen() {
      AppApi.post("lauf/abbrechen/", { id: this.lauf.id }, OHNE_UMLEITUNG)
        .then(() => this.standLaden())
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    // Derselbe Ablauf, dieselbe Laufart, dasselbe Objekt und Kennzeichen Testlauf, ohne Parameter (sie werden nicht gespeichert)
    erneutVersuchen() {
      this.anfordernHandler({
        ablauf: this.lauf.ablauf,
        laufart: this.lauf.laufart,
        objektArt: this.lauf.objektArt,
        objektId: this.lauf.objektId,
        test: this.lauf.test,
      });
    },
    // Eine Seite einer Liste laden und anhängen
    seiteLaden(liste) {
      const params = { lauf: this.lauf.id, reiter: liste.reiter, seite: liste.seite };
      if (liste.aktion) params.aktion = liste.aktion;
      return AppApi.get("lauf/eintraege/", { ...OHNE_UMLEITUNG, params }).then((response) => {
        liste.eintraege.push(...response.data.eintraege);
        liste.seite += 1;
        liste.gesamt = response.data.gesamtanzahl;
        return response.data;
      });
    },
    // Ergebnis: Fehler und nicht verarbeitete aufgeklappt, geschrieben je Aktion und verworfen zugeklappt
    ergebnisLaden() {
      const ergebnis = this.ergebnis;
      const geschrieben = AppApi.get("lauf/eintraege/", {
        ...OHNE_UMLEITUNG,
        params: { lauf: this.lauf.id, reiter: "GESCHRIEBEN", seite: 0 },
      });
      Promise.all([this.seiteLaden(ergebnis.fehler), this.seiteLaden(ergebnis.nichtVerarbeitet), geschrieben])
        .then(([fehler, , g]) => {
          const aktionen = this.stand.aktionen || {};
          const gruppen = Object.entries(g.data.anzahlJeAktion)
            .filter(([, anzahl]) => anzahl > 0)
            .map(([aktion, anzahl]) => ({
              key: "GESCHRIEBEN:" + aktion,
              reiter: "GESCHRIEBEN",
              anzahl,
              text: "geschrieben: " + (aktionen[aktion] || aktion),
              offen: false,
              liste: neueListe("GESCHRIEBEN", aktion),
            }));
          const verworfen = fehler.anzahlJeReiter.VERWORFEN || 0;
          if (verworfen > 0) {
            gruppen.push({ key: "VERWORFEN", reiter: "VERWORFEN", anzahl: verworfen, text: "verworfen", offen: false, liste: neueListe("VERWORFEN") });
          }
          ergebnis.gruppen = gruppen;
          this.ergebnisGeladen = true;
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    hatMehr(liste) {
      return liste.eintraege.length < liste.gesamt;
    },
    weitereLaden(liste) {
      this.seiteLaden(liste).catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    gruppeUmschalten(gruppe) {
      gruppe.offen = !gruppe.offen;
      if (gruppe.offen && gruppe.liste.eintraege.length === 0) this.weitereLaden(gruppe.liste);
    },
    // Tabelle: seitenweise beim Scrollen, Reiter, Filter und Suche rechnet der Server
    tabelleNachladen($state) {
      if (!this.lauf) return $state.complete();
      const t = this.tabelle;
      const infiniteId = t.infiniteId;
      const params = { lauf: this.lauf.id, reiter: t.reiter, seite: t.seite };
      if (t.objektArt) params.objektArt = t.objektArt;
      if (t.aktion) params.aktion = t.aktion;
      if (t.suche) params.suche = t.suche;
      AppApi.get("lauf/eintraege/", { ...OHNE_UMLEITUNG, params })
        .then((response) => {
          // Antwort zu einer älteren Liste (Reiter, Filter oder Suche gewechselt, Lauf neu geöffnet): verwerfen
          if (t !== this.tabelle || infiniteId !== t.infiniteId) return;
          t.eintraege.push(...response.data.eintraege);
          t.seite += 1;
          t.anzahlJeReiter = response.data.anzahlJeReiter;
          t.anzahlJeAktion = response.data.anzahlJeAktion;
          if (response.data.eintraege.length < SEITENGROESSE) $state.complete();
          else $state.loaded();
        })
        .catch((e) => {
          this.$notify(apiErrorToAlert(e));
          $state.error();
        });
    },
    tabelleSichtbar() {
      const mitTabelle = this.zustand === "automatisch" || this.zustand === "testlauf";
      if (mitTabelle && this.tabelle.eintraege.length === 0) this.tabelleNeuBeginnen();
    },
    tabelleNeuBeginnen() {
      this.tabelle.eintraege = [];
      this.tabelle.seite = 0;
      this.tabelle.infiniteId += 1;
    },
    sucheVerzoegert: debounce((self) => {
      self.tabelleNeuBeginnen();
    }, 500),
    reiterWaehlen(key) {
      this.tabelle.reiter = key;
    },
    statusText(status) {
      if (status === "GESCHRIEBEN" && this.zustand === "testlauf") return "würde geschrieben";
      return STATUS_TEXT[status] || status;
    },
    feldName(feld) {
      return feld.feldAppKey ? this.$t("global." + feld.feldAppKey) : feld.feld;
    },
    // Links nach Objektart (G3 Schritt 7); andere Objektarten und Einträge ohne ID ohne Link
    linkZiel(eintrag) {
      const id = eintrag.objektId;
      if (!id) return null;
      if (eintrag.objektArt === "auftrag") return { name: "Auftrag", params: { id } };
      if (eintrag.objektArt === "person") return { name: "geschaeftspartner", params: { id, anzeige: 0 } };
      if (eintrag.objektArt === "firma") return { name: "firma", params: { id, anzeige: 0 } };
      return null;
    },
    objektOeffnen(eintrag) {
      const ziel = this.linkZiel(eintrag);
      if (!ziel) return;
      this.schliessen();
      this.$router.push(ziel);
    },
    darfEintragErneut(eintrag) {
      return !!(eintrag.objektId && this.stand.einzelLaufarten[eintrag.objektArt]);
    },
    // An einem Eintrag: die Laufart, die der Ablauf für seine Objektart nennt, mit dem Datensatz als Objekt
    eintragErneut(eintrag) {
      this.anfordernHandler({
        ablauf: this.lauf.ablauf,
        laufart: this.stand.einzelLaufarten[eintrag.objektArt],
        objektArt: eintrag.objektArt,
        objektId: eintrag.objektId,
        test: this.lauf.test,
      });
    },
    kopieren(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => this.$notify({ type: "success", title: "Kopiert", text }))
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    objektArtName(objektArt) {
      return OBJEKTARTEN[objektArt] || objektArt;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/scss/colors";

.kopf {
  font-size: 13px;
  color: #56616b;
  margin: 0;
}
.mono {
  font-family: Consolas, Menlo, monospace;
  font-size: 12.5px;
}
.panel {
  background: $very-light-blue;
  border-radius: 10px;
  padding: 8px 12px;
}
.schritte {
  background: $medium-blue;
  border-radius: 10px;
  padding: 4px 10px 10px;
}
.schritt {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 12px;
  align-items: center;
  background: $very-light-blue;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 6px;
  font-weight: 600;
}
.punkt {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  background: #dfe1e6;
}
.punkt-fertig,
.punkt-laeuft {
  background: $dark-blue;
}
.progress-bar {
  background-color: $dark-blue;
}
.fakten {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.fakt {
  background: $very-light-blue;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 600;
}
.fakt-titel {
  font-size: 11px;
  font-weight: 700;
}
/* Farben der Statusklassen aus _belegliste.scss (.statusWarn, .statusDanger) */
.text-bernstein {
  color: #a54800;
  font-weight: 600;
}
.text-rot {
  color: #ae2e24;
  font-weight: 600;
}
.text-rot-normal {
  color: #ae2e24;
}
.abschnitt {
  background: #fff;
  border: 1px solid #d3e0e8;
  border-radius: 10px;
  overflow: hidden;
}
.abschnitt-kopf {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 1rem;
  .abschnitt-titel {
    font-family: "Open Sans", Arial, sans-serif;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }
}
.klickbar {
  cursor: pointer;
}
.abschnitt-inhalt {
  padding: 0 1rem 0.7rem 3rem;
}
.symbol {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex: none;
}
.symbol-rot {
  background: #ffeceb;
  color: #ae2e24;
}
.symbol-bernstein {
  background: #fff7d6;
  color: #a54800;
}
.symbol-grau {
  background: #e6eef3;
  color: #2c4655;
}
.zeile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.5rem 0;
  border-top: 1px solid #e6eef3;
}
.zeile-text {
  flex: 1;
  min-width: 0;
}
/* Eigener Scrollbereich: darin lädt vue-infinite-loading beim Scrollen nach */
.tabelle-scroll {
  max-height: 55vh;
  overflow-y: auto;
}
.reiter .nav-link {
  font-size: 18px;
}
.reiter-zahl {
  font-family: "Open Sans", Arial, sans-serif;
  font-size: 12px;
  margin-left: 5px;
  color: #3d4a55;
}
/* Alt und neu mit den Farben des Audit-Panels */
.wert-alt {
  background: $very-light-red;
  color: #c62f26;
  text-decoration: line-through;
  border-radius: 3px;
  padding: 1px 6px;
}
.wert-neu {
  background: $very-light-green;
  color: #3e7a3a;
  border-radius: 3px;
  padding: 1px 6px;
}
</style>
